import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { isConfiguredVoucher } from '@ibk/shared-utils';
import { Icon } from '../Icon';
import styles from './BenefitCards.module.css';
import { ActiveBenefitCard, InactiveBenefitCard } from './components';
import { getDiscountValue, getIconName, getRechargeDayDescription, getTitleKey, getValueWithTranslation, } from './formatters';
const VISIBLE_CARDS_THRESHOLD = 3;
const SCROLL_DEBOUNCE_MS = 100;
export const createInactiveBenefitCard = ({ card, t, onConfigure }) => ({
    id: card.voucherTypeDescription,
    title: t(getTitleKey(card.voucherTypeDescription)),
    description: t('benefit-card.description.default'),
    iconName: getIconName(card.voucherTypeDescription),
    onConfigure: () => onConfigure?.(card.voucherTypeDescription),
});
export const createActiveBenefitCard = ({ card, t, onConfigure }) => ({
    id: card.voucherTypeDescription,
    title: t(getTitleKey(card.voucherTypeDescription)),
    iconName: getIconName(card.voucherTypeDescription),
    creditRelease: getRechargeDayDescription(t, card.rechargeDayType, card.rechargeDay),
    beneficiaries: card.totalBeneficiaries || 0,
    value: getValueWithTranslation(card, t),
    discount: getDiscountValue(card.discountType, card.discountValue, t),
    onConfigure: () => onConfigure?.(card.voucherTypeDescription),
});
const BenefitCards = ({ cards, 'data-testid': dataTestId, onConfigure }) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef(null);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const processedCards = useMemo(() => {
        return cards.map(card => {
            const isActive = isConfiguredVoucher(card);
            return {
                id: card.voucherTypeDescription,
                isActive,
                data: isActive
                    ? createActiveBenefitCard({ card, t, onConfigure })
                    : createInactiveBenefitCard({ card, t, onConfigure }),
            };
        });
    }, [cards, t, onConfigure]);
    const hasMoreThanThreeCards = processedCards.length > VISIBLE_CARDS_THRESHOLD;
    const shouldExpandCards = processedCards.length < VISIBLE_CARDS_THRESHOLD;
    const scrollTimeoutRef = useRef(null);
    const updateArrowVisibility = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container)
            return;
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const hasMoreToScroll = scrollLeft + clientWidth < scrollWidth - 1;
        const hasMoreToScrollLeft = scrollLeft > 1;
        setShowRightArrow(hasMoreToScroll);
        setShowLeftArrow(hasMoreToScrollLeft);
    }, []);
    const handleScroll = useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            updateArrowVisibility();
        }, SCROLL_DEBOUNCE_MS);
    }, [updateArrowVisibility]);
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);
    const handleRightArrowClick = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container)
            return;
        const cardWidth = container.clientWidth / VISIBLE_CARDS_THRESHOLD;
        const maxScroll = container.scrollWidth - container.clientWidth;
        const currentScroll = container.scrollLeft;
        const currentCardIndex = Math.round(currentScroll / cardWidth);
        const targetCardIndex = currentCardIndex + 1;
        const targetScroll = Math.min(targetCardIndex * cardWidth, maxScroll);
        container.scrollTo({
            left: targetScroll,
            behavior: 'smooth',
        });
    }, []);
    const handleLeftArrowClick = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container)
            return;
        const cardWidth = container.clientWidth / VISIBLE_CARDS_THRESHOLD;
        const currentScroll = container.scrollLeft;
        const currentCardIndex = Math.round(currentScroll / cardWidth);
        const targetCardIndex = currentCardIndex - 1;
        const targetScroll = Math.max(targetCardIndex * cardWidth, 0);
        container.scrollTo({
            left: targetScroll,
            behavior: 'smooth',
        });
    }, []);
    useEffect(() => {
        updateArrowVisibility();
    }, [processedCards.length, updateArrowVisibility]);
    if (!processedCards.length) {
        return null;
    }
    return (_jsxs("div", { className: styles.carouselWrapper, "data-testid": dataTestId ?? 'domain-cards', children: [hasMoreThanThreeCards && (_jsx("button", { type: 'button', className: `${styles.carouselArrow} ${styles.carouselArrowLeft} ${showLeftArrow ? '' : styles.carouselArrowHidden}`, onClick: handleLeftArrowClick, "data-testid": `${dataTestId ?? 'domain-cards'}-arrow-left`, "aria-label": t('benefit-card.carousel.previous'), disabled: !showLeftArrow, children: _jsx(Icon, { name: 'arrowChevronLeft', className: styles.arrowIcon }) })), _jsx("div", { ref: scrollContainerRef, className: styles.container, onScroll: handleScroll, "data-testid": `${dataTestId ?? 'domain-cards'}-scroll-container`, children: processedCards.map((processedCard, index) => (_jsxs(React.Fragment, { children: [processedCard.isActive ? (_jsx(ActiveBenefitCard, { card: processedCard.data, expanded: shouldExpandCards })) : (_jsx(InactiveBenefitCard, { card: processedCard.data, expanded: shouldExpandCards })), index < processedCards.length - 1 && (_jsx("div", { className: styles.divider, "data-testid": `domain-cards-divider-${index}` }))] }, processedCard.id))) }), hasMoreThanThreeCards && (_jsx("button", { type: 'button', className: `${styles.carouselArrow} ${styles.carouselArrowRight} ${showRightArrow ? '' : styles.carouselArrowHidden}`, onClick: handleRightArrowClick, "data-testid": `${dataTestId ?? 'domain-cards'}-arrow-right`, "aria-label": t('benefit-card.carousel.next'), disabled: !showRightArrow, children: _jsx(Icon, { name: 'arrowChevronRight', className: styles.arrowIcon }) }))] }));
};
export default BenefitCards;
